import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { Modal, Select, Input, Button, Typography, Form, InputNumber, DatePicker, message } from 'antd';
import { Briefcase, X, Save, TrendingUp, Calendar, User, Percent } from 'lucide-react';
import dialerService from '../services/dialerService';
import { getSession } from '../../../getSession';

const { Text } = Typography;
const { TextArea } = Input;

const DealModalV2 = ({ isOpen, onClose, record }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [pipelines, setPipelines] = useState([]);
  const [stages, setStages] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (isOpen && record) {
      fetchPipelines();
      fetchUsers();
      form.resetFields();
      form.setFieldsValue({
        dealName: `Deal_${record.PersonName || 'Customer'}_${dayjs().format('M/D/YYYY')}`,
        currency: 'INR',
        probability: 100,
        closureDate: dayjs().add(7, 'day'),
        owner: String(getSession().userId)
      });
    }
  }, [isOpen, record]);

  const fetchUsers = async () => {
    try {
      const res = await dialerService.getHierarchyUsers();
      if (res) setUsers(res.map(u => ({ label: u.Text, value: String(u.Value || u.Id) })));
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchPipelines = async () => {
    try {
      const res = await dialerService.getDealPipelines();
      if (res) setPipelines(res.map(p => ({ label: p.Text, value: String(p.Id || p.Value || p.Code) })));
    } catch (error) {
      console.error('Failed to fetch pipelines:', error);
    }
  };

  const handlePipelineChange = async (value) => {
    try {
      const res = await dialerService.getDealPipelineStages(value);
      if (res) setStages(res.map(s => ({ label: s.Text, value: String(s.Id || s.Value || s.Code) })));
    } catch (error) {
      console.error('Failed to fetch stages:', error);
    }
  };

  const handleSave = async (values) => {
    setLoading(true);
    try {
      const { parentId } = getSession();
      const payload = {
        LeadId: record.Id,
        PipelineId: values.pipeline,
        PipelineStage: values.stage,
        Amount: values.amount || 0,
        ClosureDate: values.closureDate ? values.closureDate.format('MM/DD/YYYY') : '',
        DealOwner: values.owner,
        Probability: values.probability || 0,
        UserId: parentId,
        LostId: 0 // Defaulting to 0 as reasons aren't implemented yet
      };

      const res = await dialerService.saveDealData(payload);

      if (res === "Deal Saved") {
        message.success("Deal Saved Successfully!");
        onClose();
      } else if (res === "LeadID Already Occupied") {
        message.warning("This lead is already occupied in the current pipeline stage.");
      } else {
        message.error(res || "Failed to save deal data.");
      }
    } catch (error) {
      console.error('Failed to save deal:', error);
      message.error("An error occurred while saving the deal.");
    } finally {
      setLoading(false);
    }
  };

  if (!record) return null;

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <Briefcase size={20} className="text-amber-600" />
          <span className="text-lg font-bold text-slate-800">Add Deal</span>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={550}
      className="premium-modal"
      closeIcon={<X size={20} className="text-slate-400 hover:text-slate-600" />}
      centered
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        className="py-4"
      >
        <div className="space-y-6">
          <Form.Item
            name="dealName"
            label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Deal Name</span>}
            rules={[{ required: true, message: 'Enter deal name' }]}
          >
            <Input placeholder="Enter deal name" className="h-11 rounded-xl" />
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="pipeline"
              label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Pipeline</span>}
              rules={[{ required: true, message: 'Select pipeline' }]}
            >
              <Select
                placeholder="Select Pipeline"
                options={pipelines}
                onChange={handlePipelineChange}
                className="h-11 premium-select"
                getPopupContainer={() => document.body}
                virtual={false}
              />
            </Form.Item>

            <Form.Item
              name="stage"
              label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Stage</span>}
              rules={[{ required: true, message: 'Select stage' }]}
            >
              <Select
                placeholder="Select Stage"
                options={stages}
                className="h-11 premium-select"
                getPopupContainer={() => document.body}
                virtual={false}
              />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="amount"
              label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1">Deal Amount</span>}
            >
              <InputNumber
                className="w-full h-11 rounded-xl flex items-center"
                placeholder="0.00"
                formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/\₹\s?|(,*)/g, '')}
              />
            </Form.Item>

            <Form.Item
              name="probability"
              label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Probability (%)</span>}
              rules={[
                { required: true, message: 'Enter probability' },
                { type: 'number', min: 0, max: 100, message: 'Range 0-100' }
              ]}
            >
              <InputNumber
                className="w-full h-11 rounded-xl flex items-center"
                placeholder="100"
                min={0}
                max={100}
                addonAfter={<Percent size={14} />}
              />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="owner"
              label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Deal Owner</span>}
              rules={[{ required: true, message: 'Select owner' }]}
            >
              <Select
                placeholder="Select Owner"
                options={users}
                className="h-11 premium-select"
                getPopupContainer={() => document.body}
                virtual={false}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>

            <Form.Item
              name="closureDate"
              label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Closure Date</span>}
              rules={[{ required: true, message: 'Select date' }]}
            >
              <DatePicker
                className="w-full h-11 rounded-xl"
                format="MM/DD/YYYY"
                getPopupContainer={() => document.body}
              />
            </Form.Item>
          </div>

          <Form.Item
            name="description"
            label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1">Description</span>}
          >
            <TextArea rows={3} placeholder="Deal details..." className="rounded-xl" />
          </Form.Item>
        </div>

        <div className="flex justify-end gap-3 mt-8 border-t border-slate-100 pt-6">
          <Button onClick={onClose} className="rounded-xl font-bold h-11 px-6">
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            icon={<TrendingUp size={16} />}
            className="rounded-xl font-bold h-11 px-10 bg-amber-600 hover:bg-amber-700 shadow-lg shadow-amber-200 border-none flex items-center gap-2"
          >
            Create Deal
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default DealModalV2;
